'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { ScanLine, Keyboard } from 'lucide-react';

import { useRouter } from '@/i18n/routing';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQrCameraScanner } from '@/hooks/use-qr-camera-scanner';
import { useQrNavigateSearch } from '@/hooks/use-qr-navigate-search';
import { useQrLiveSearch } from '@/hooks/use-qr-live-search';
import type { SearchableItem } from '@/hooks/use-qr-live-search';
import { toast } from 'sonner';
import { useRouteAccess } from '@/hooks/use-route-access';
import { IS_QR_SCANNER_ENABLED } from '@/lib/feature-flags';
import { QrCameraTabContent } from './qr-camera-tab';
import { QrManualTabContent } from './qr-manual-tab';

interface QrScanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage?: string;
  onNavigate?: (id: number) => void;
  /** When provided, scanning/manual input calls this directly with the container number (skips registration search). */
  onScanValue?: (containerNumber: string) => void;
  translationNamespace?: string;
  /** Pre-loaded items for client-side filtering in manual input (used with onScanValue). Bypasses registration search. */
  searchableItems?: SearchableItem[];
}

const SCANNER_CONTAINER_ID = 'qr-scanner-region';

export function QrScanDialog(props: QrScanDialogProps) {
  // Wrapper gate: when the system-wide flag is off the inner component (and its
  // camera-startup effects) never mount. Defense in depth on top of trigger-button
  // gating in each feature.
  if (!IS_QR_SCANNER_ENABLED) return null;
  return <QrScanDialogInner {...props} />;
}

function QrScanDialogInner({
  open,
  onOpenChange,
  stage = 'sticking',
  onNavigate,
  onScanValue,
  translationNamespace = 'sticking',
  searchableItems
}: QrScanDialogProps) {
  const t = useTranslations(translationNamespace);
  const tg = useTranslations('auth.guard');
  const router = useRouter();
  const canAccess = useRouteAccess();
  const [manualInput, setManualInput] = useState('');
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [activeTab, setActiveTab] = useState('camera');
  const [scannedItems, setScannedItems] = useState<string[]>([]);
  const prevOpenRef = useRef(open);

  // Wrap onScanValue to also accumulate scannedItems history
  const handleCameraScan = useCallback(
    (value: string) => {
      setScannedItems((prev) => [value, ...prev]);
      onScanValue?.(value);
    },
    [onScanValue]
  );

  const {
    scannerReady,
    scannerError,
    scannedValue,
    clearScannedValue,
    startScanner,
    stopScanner
  } = useQrCameraScanner({
    containerId: SCANNER_CONTAINER_ID,
    stage,
    onScanValue: onScanValue ? handleCameraScan : undefined,
    t: t as (key: string) => string
  });

  const searchInput = scannedValue || manualInput;
  const navigateEnabled =
    !onScanValue &&
    searchInput.length > 0 &&
    (!!scannedValue || searchTriggered);

  const { results, isFetching } = useQrNavigateSearch({
    searchInput,
    stage,
    enabled: navigateEnabled
  });

  const { liveResults, isLiveSearching } = useQrLiveSearch({
    manualInput,
    stage,
    enabled: !!onScanValue,
    searchableItems
  });

  // Auto-navigate after camera scan finds a match (navigate mode)
  useEffect(() => {
    if (!onScanValue && scannedValue && !isFetching && results.length > 0) {
      handleNavigate(results[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannedValue, isFetching, results.length]);

  // Start/stop scanner based on dialog open state and active tab
  useEffect(() => {
    if (open && activeTab === 'camera') {
      setSearchTriggered(false);
      const id = requestAnimationFrame(() => {
        startScanner();
      });
      return () => {
        cancelAnimationFrame(id);
        stopScanner();
      };
    }
    return () => {
      stopScanner();
    };
  }, [open, activeTab, startScanner, stopScanner]);

  // Reset state when dialog closes
  useEffect(() => {
    if (prevOpenRef.current && !open) {
      clearScannedValue();
      requestAnimationFrame(() => {
        setManualInput('');
        setSearchTriggered(false);
        setScannedItems([]);
      });
    }
    prevOpenRef.current = open;
  }, [open, clearScannedValue]);

  function handleScanValueComplete(containerNumber: string) {
    setScannedItems((prev) => [containerNumber, ...prev]);
    onScanValue?.(containerNumber);
    setManualInput('');
    setSearchTriggered(false);
    clearScannedValue();
  }

  function handleManualSearch() {
    if (!manualInput.trim()) return;
    if (onScanValue) {
      handleScanValueComplete(manualInput.trim());
      return;
    }
    clearScannedValue();
    setSearchTriggered(true);
  }

  function handleNavigate(registrationId: number) {
    stopScanner();
    onOpenChange(false);
    setManualInput('');
    setSearchTriggered(false);
    clearScannedValue();
    if (onNavigate) {
      onNavigate(registrationId);
    } else {
      const stageRoutes: Record<string, string> = {
        sticking: '/dashboard/sticking',
        review: `/dashboard/review/${registrationId}`
      };
      // `containers`, plural — the singular spelling matched no route and 404'd.
      const target =
        stageRoutes[stage] ?? `/dashboard/containers/${registrationId}`;
      // The dialog is mounted on several stage screens and can resolve a code
      // belonging to another one, so check before leaving the current page.
      if (!canAccess(target)) {
        toast.error(tg('noPermission'));
        return;
      }
      router.push(target);
    }
  }

  function handleScanAgain() {
    clearScannedValue();
    setSearchTriggered(false);
    startScanner();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>{t('scanQRTitle')}</DialogTitle>
          <DialogDescription>{t('scanQRDescription')}</DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue='camera'
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className='w-full'>
            <TabsTrigger value='camera' className='flex-1'>
              <ScanLine className='me-2 h-4 w-4' />
              {t('cameraTab')}
            </TabsTrigger>
            <TabsTrigger value='manual' className='flex-1'>
              <Keyboard className='me-2 h-4 w-4' />
              {t('manualTab')}
            </TabsTrigger>
          </TabsList>

          <QrCameraTabContent
            scannerContainerId={SCANNER_CONTAINER_ID}
            scannerReady={scannerReady}
            scannerError={scannerError}
            scannedValue={scannedValue}
            scannedItems={scannedItems}
            results={results}
            isFetching={isFetching}
            searchTriggered={searchTriggered}
            isOnScanValue={!!onScanValue}
            onNavigate={handleNavigate}
            onScanAgain={handleScanAgain}
            onStartScanner={startScanner}
            t={t as (key: string) => string}
          />

          <QrManualTabContent
            manualInput={manualInput}
            onManualInputChange={setManualInput}
            onSearch={handleManualSearch}
            isFetching={isFetching}
            searchTriggered={searchTriggered}
            results={results}
            liveResults={liveResults}
            isLiveSearching={isLiveSearching}
            searchableItems={searchableItems}
            isOnScanValue={!!onScanValue}
            stage={stage}
            onNavigate={handleNavigate}
            onSelectLiveResult={handleScanValueComplete}
            t={t as (key: string) => string}
          />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
